import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { authAxios } from "../utils/auth";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Brain, Pencil, Save, X } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import AdminMenu from "../components/AdminMenu";

// 16 Learning Profile Categories with descriptions
const PROFILE_CATEGORY_DESCRIPTIONS = {
  // Original 12 categories
  core_identity: "Who you are at your core, what feels most true to you, and how you naturally show up in the world.",
  communication_style: "How you express yourself, share thoughts and feelings, and connect through words, tone, and presence.",
  love_language: "How you give and receive affection, feel valued, and experience closeness in relationships.",
  lifestyle_interests: "How you live day to day, what excites you, and the passions that shape your rhythm of life.",
  professional_work: "How you approach work, purpose, ambition, and the role your career plays in your identity.",
  social_dynamics: "How you interact with others, navigate groups, and build connection in social settings.",
  values_beliefs: "What truly matters to you, the principles you live by, and the beliefs that guide your choices.",
  attractions_desires: "What draws you in, sparks curiosity, and creates chemistry and longing.",
  dislikes_boundaries: "What feels misaligned, where your limits are, and what you need to feel safe and respected.",
  emotional_patterns: "How you experience emotions, respond to stress, and move through emotional highs and lows.",
  aesthetic_expression: "How you express yourself visually, creatively, and through personal style and presence.",
  cultural_background: "The experiences, heritage, and upbringing that have shaped who you are today.",
  // New 4 categories
  skills_aptitudes: "Core strengths, natural talents, and areas for growth or development.",
  relationship_ecosystem: "Key personal connections including family, friends, partners/ex-partners, children, co-parents, and animals/pets.",
  physical_mental_wellbeing: "Health considerations, psychological patterns, neurodiversity, and overall wellbeing factors.",
  leadership_orientation: "Preferred leadership style, decision-making approach, and how you lead, follow, and guide others."
};

const PROFILE_CATEGORY_NAMES = {
  // Original 12 categories
  core_identity: "Core Identity",
  communication_style: "Communication Style",
  love_language: "Love Language (Detailed)",
  lifestyle_interests: "Lifestyle & Interests",
  professional_work: "Professional & Work Life",
  social_dynamics: "Social Dynamics",
  values_beliefs: "Values & Beliefs",
  attractions_desires: "Attractions & Desires",
  dislikes_boundaries: "Dislikes & Boundaries",
  emotional_patterns: "Emotional Patterns",
  aesthetic_expression: "Aesthetic & Expression",
  cultural_background: "Cultural & Background",
  // New 4 categories
  skills_aptitudes: "Skills & Aptitudes",
  relationship_ecosystem: "Relationship Ecosystem",
  physical_mental_wellbeing: "Physical & Mental Wellbeing",
  leadership_orientation: "Leadership Orientation"
};

const LearningProfileDetail = () => {
  const navigate = useNavigate();
  const { profileId } = useParams();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [dataSources, setDataSources] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchDataSources = async (ownerType, ownerId) => {
      try {
        const response = await authAxios.get("/data-sources", {
          params: { owner_type: ownerType, owner_id: ownerId }
        });
        setDataSources(response.data);
      } catch (error) {
        console.error("Error fetching data sources:", error);
      }
    };

    const fetchProfile = async () => {
      try {
        const response = await authAxios.get(`/learning-profiles/${profileId}`);
        setProfile(response.data);
        
        // Fetch data sources if IDs exist
        if (response.data.source_data_ids && response.data.source_data_ids.length > 0) {
          fetchDataSources(response.data.owner_type, response.data.owner_id);
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching profile:", error);
        toast.error("Profile not found");
        navigate(-1);
      }
    };

    fetchProfile();
  }, [profileId, navigate]);

  const handleUpdatePriority = async (newPriority) => {
    try {
      await authAxios.put(`/learning-profiles/${profileId}`, {
        priority: parseInt(newPriority)
      });
      
      setProfile({ ...profile, priority: parseInt(newPriority) });
      toast.success("Priority updated");
    } catch (error) {
      console.error("Error updating priority:", error);
      toast.error("Failed to update priority");
    }
  };

  const handleEdit = () => {
    setEditedContent(profile?.content || "");
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedContent("");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await authAxios.put(`/learning-profiles/${profileId}`, {
        content: editedContent
      });
      setProfile({ ...profile, content: editedContent });
      setIsEditing(false);
      toast.success("Profile saved");
    } catch (error) {
      console.error("Error saving profile:", error);
      toast.error("Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    // Navigate back to the appropriate learning resources tab
    if (profile?.owner_type === "relationship" && profile?.owner_id) {
      navigate(`/relationships/${profile.owner_id}`, { state: { activeTab: 'learning' } });
    } else if (profile?.owner_type === "user") {
      navigate('/profile', { state: { activeTab: 'learning' } });
    } else {
      navigate(-1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-divine-black flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-divine-gold border-t-transparent rounded-full" />
      </div>
    );
  }

  const categoryName = PROFILE_CATEGORY_NAMES[profile?.profile_category] || profile?.profile_category;
  const categoryDescription = PROFILE_CATEGORY_DESCRIPTIONS[profile?.profile_category] || "";

  return (
    <div className="min-h-screen bg-divine-black">
      {/* Header */}
      <header className="border-b border-white/10 bg-divine-black-light/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={handleBack}
                className="text-white/60 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <Brain className="w-6 h-6 text-divine-gold" />
                <div>
                  <h1 className="font-cinzel text-xl text-white">
                    {categoryName}
                  </h1>
                  <p className="text-white/50 text-sm max-w-md">
                    {categoryDescription}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Priority Selector */}
              <Select
                value={(profile?.priority || 2).toString()}
                onValueChange={handleUpdatePriority}
                disabled={isEditing}
              >
                <SelectTrigger className="w-[150px] bg-divine-black border-divine-gold/30 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-divine-charcoal border-divine-gold/30">
                  <SelectItem value="1">Learning</SelectItem>
                  <SelectItem value="2">Moderate</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Edit/Save/Cancel Buttons */}
              {isEditing ? (
                <>
                  <Button
                    variant="ghost"
                    onClick={handleCancel}
                    className="text-white/60 hover:text-white hover:bg-white/10"
                    disabled={saving}
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSave}
                    className="bg-divine-gold text-divine-black hover:bg-divine-gold/90"
                    disabled={saving}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? "Saving..." : "Save"}
                  </Button>
                </>
              ) : (
                <Button
                  variant="ghost"
                  onClick={handleEdit}
                  className="text-divine-gold hover:text-divine-gold hover:bg-divine-gold/10"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              )}
              <AdminMenu />
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-6 py-8">
        <Card className="bg-divine-charcoal border-divine-gold/20">
          <CardHeader>
            <CardTitle className="text-divine-gold font-cinzel">Agent Eros Analysis</CardTitle>
          </CardHeader>
          <CardContent>
            {isEditing ? (
              <Textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                className="min-h-[400px] bg-divine-black border-divine-gold/30 text-white/90 leading-relaxed resize-y"
                placeholder="Enter analysis content..."
              />
            ) : (
              <div 
                className="prose prose-invert max-w-none leading-relaxed"
                style={{
                  '--tw-prose-headings': 'var(--divine-gold)',
                  '--tw-prose-bold': 'var(--divine-gold)',
                  '--tw-prose-links': 'var(--divine-gold)',
                  '--tw-prose-bullets': 'var(--divine-gold)',
                  '--tw-prose-counters': 'var(--divine-gold)',
                  '--tw-prose-quote-borders': 'var(--divine-gold)',
                }}
              >
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({children}) => (
                      <h1 className="text-2xl font-cinzel pb-2 mb-4 border-b" style={{color: 'var(--divine-gold)', borderColor: 'var(--border-primary)'}}>{children}</h1>
                    ),
                    h2: ({children}) => (
                      <h2 className="text-xl font-cinzel mt-6 mb-3" style={{color: 'var(--divine-gold)', opacity: 0.9}}>{children}</h2>
                    ),
                    h3: ({children}) => (
                      <h3 className="text-lg font-cinzel mt-4 mb-2" style={{color: 'var(--divine-gold)', opacity: 0.8}}>{children}</h3>
                    ),
                    p: ({children}) => (
                      <p className="mb-4" style={{color: 'var(--text-secondary)'}}>{children}</p>
                    ),
                    strong: ({children}) => (
                      <strong className="font-semibold" style={{color: 'var(--divine-gold)'}}>{children}</strong>
                    ),
                    em: ({children}) => (
                      <em className="italic" style={{color: 'var(--text-secondary)'}}>{children}</em>
                    ),
                    ul: ({children}) => (
                      <ul className="my-3 ml-4 list-disc" style={{color: 'var(--text-secondary)'}}>{children}</ul>
                    ),
                    ol: ({children}) => (
                      <ol className="my-3 ml-4 list-decimal" style={{color: 'var(--text-secondary)'}}>{children}</ol>
                    ),
                    li: ({children}) => (
                      <li className="mb-1" style={{'--tw-prose-bullets': 'var(--divine-gold)'}}>{children}</li>
                    ),
                    blockquote: ({children}) => (
                      <blockquote className="border-l-4 px-4 py-2 my-4 italic" style={{borderColor: 'var(--divine-gold)', backgroundColor: 'var(--bg-glass)', color: 'var(--text-secondary)'}}>{children}</blockquote>
                    ),
                    code: ({children}) => (
                      <code className="px-1.5 py-0.5 rounded text-sm" style={{backgroundColor: 'var(--bg-secondary)', color: 'var(--accent-primary)'}}>{children}</code>
                    ),
                    hr: () => (
                      <hr className="my-6" style={{borderColor: 'var(--border-primary)'}} />
                    ),
                    table: ({children}) => (
                      <div className="overflow-x-auto my-4">
                        <table className="w-full border-collapse" style={{borderColor: 'var(--border-primary)'}}>
                          {children}
                        </table>
                      </div>
                    ),
                    thead: ({children}) => (
                      <thead style={{backgroundColor: 'var(--bg-glass-strong)', borderBottom: '2px solid var(--divine-gold)'}}>
                        {children}
                      </thead>
                    ),
                    tbody: ({children}) => (
                      <tbody>{children}</tbody>
                    ),
                    tr: ({children}) => (
                      <tr className="border-b" style={{borderColor: 'var(--border-primary)'}}>
                        {children}
                      </tr>
                    ),
                    th: ({children}) => (
                      <th className="px-4 py-2 text-left font-semibold" style={{color: 'var(--divine-gold)'}}>
                        {children}
                      </th>
                    ),
                    td: ({children}) => (
                      <td className="px-4 py-2" style={{color: 'var(--text-secondary)'}}>
                        {children}
                      </td>
                    ),
                  }}
                >
                  {profile?.content || "No content available."}
                </ReactMarkdown>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Data Sources Used */}
        {profile?.source_data_ids && profile.source_data_ids.length > 0 && (
          <Card className="bg-divine-charcoal border-divine-gold/20 mt-6">
            <CardHeader>
              <CardTitle className="text-divine-gold font-cinzel text-lg">Data Sources Used</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-white/60 text-sm mb-4">
                This profile was generated from {profile.source_data_ids.length} data source(s):
              </p>
              
              {dataSources.length > 0 ? (
                <div className="space-y-2">
                  {dataSources
                    .filter(ds => profile.source_data_ids.includes(ds.source_id))
                    .map((source) => {
                      // Find matching snapshot for this source to get processed_at
                      const snapshot = profile.source_snapshots?.find(s => s.source_id === source.source_id);
                      const sourceDate = source.updated_at || source.created_at;
                      
                      return (
                        <div key={source.source_id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-divine-gold/20">
                          <div className="flex items-center gap-3">
                            <div className={`w-2 h-2 rounded-full ${source.auto_imported ? 'bg-cyan-400' : 'bg-divine-gold'}`} />
                            <div>
                              <p className="text-white text-sm font-medium">{source.title}</p>
                              <p className="text-white/40 text-xs">
                                {source.char_count?.toLocaleString() || 0} characters
                                {source.auto_imported && " • Auto-imported"}
                              </p>
                              {sourceDate && (
                                <p className="text-white/30 text-xs mt-0.5">
                                  Source updated: {new Date(sourceDate).toLocaleString()}
                                </p>
                              )}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/data-source/${source.source_id}`)}
                            className="text-divine-gold hover:bg-divine-gold/10"
                          >
                            View
                          </Button>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <p className="text-white/50 text-sm">Loading data sources...</p>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default LearningProfileDetail;
